import { useEffect, useState } from "react";
import api from "@/lib/api";
import { apiError } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ShieldCheck } from "@phosphor-icons/react";

const ROLES = [
  { key: "admin", label: "Admin" },
  { key: "manager", label: "Manager" },
  { key: "member", label: "Member" },
];

const FEATURES = [
  { key: "create_job", label: "Create New Job" },
  { key: "edit_delete_job", label: "Edit/Delete Job" },
  { key: "approve_drawings", label: "Approve Drawings" },
  { key: "create_purchase_orders", label: "Create Purchase Orders" },
];

export default function FeaturePermissionsGrid() {
  const [roles, setRoles] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/feature-permissions");
      setRoles(data.roles);
    } catch (requestError) {
      setError(apiError(requestError.response?.data?.detail) || "Could not load feature permissions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const toggle = (role, feature) => {
    setRoles((current) => ({
      ...current,
      [role]: {
        ...current[role],
        [feature]: !current[role][feature],
      },
    }));
  };

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const { data } = await api.put("/feature-permissions", { roles });
      setRoles(data.roles);
      toast.success("Feature permissions saved");
    } catch (requestError) {
      const message = apiError(requestError.response?.data?.detail) || "Could not save permissions";
      setError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <section className="border border-zinc-800 bg-zinc-900/30 p-5" data-testid="feature-permissions-loading">
        <p className="text-sm text-zinc-400">Loading feature permissions…</p>
      </section>
    );
  }

  if (error && !roles) {
    return (
      <section className="border border-rose-900 bg-rose-950/30 p-5" data-testid="feature-permissions-error">
        <p className="text-sm text-rose-300">{error}</p>
      </section>
    );
  }

  return (
    <section className="max-w-4xl border border-zinc-800 bg-zinc-900/30" data-testid="feature-permissions-grid">
      <div className="border-b border-zinc-800 px-5 py-4">
        <div className="flex items-center gap-2 text-zinc-100">
          <ShieldCheck size={19} className="text-emerald-400" />
          <h2 className="font-heading text-lg font-bold">Feature Permissions</h2>
        </div>
        <p className="mt-1 text-xs text-zinc-500">
          These controls govern live write actions. Users keep read-only project, PO, and drawing status visibility.
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[620px] text-sm">
          <thead className="border-b border-zinc-800 bg-zinc-950/60 text-left text-[10px] font-bold uppercase tracking-wider text-zinc-500">
            <tr>
              <th className="px-5 py-3">Feature</th>
              {ROLES.map((role) => (
                <th key={role.key} className="px-5 py-3 text-center">{role.label}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800/70">
            {FEATURES.map((feature) => (
              <tr key={feature.key}>
                <td className="px-5 py-3 font-semibold text-zinc-200">{feature.label}</td>
                {ROLES.map((role) => (
                  <td key={role.key} className="px-5 py-3 text-center">
                    <Checkbox
                      checked={Boolean(roles?.[role.key]?.[feature.key])}
                      onCheckedChange={() => toggle(role.key, feature.key)}
                      className="border-zinc-600 data-[state=checked]:border-emerald-500 data-[state=checked]:bg-emerald-600"
                      data-testid={`feature-permission-${feature.key}-${role.key}`}
                      aria-label={`${role.label}: ${feature.label}`}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-zinc-800 px-5 py-4">
        {error ? <p className="text-xs text-rose-300" data-testid="feature-permissions-save-error">{error}</p> : <span />}
        <Button
          type="button"
          onClick={save}
          disabled={saving}
          className="bg-emerald-600 text-white hover:bg-emerald-700"
          data-testid="feature-permissions-save"
        >
          {saving ? "Saving…" : "Save Feature Permissions"}
        </Button>
      </div>
    </section>
  );
}